import { buildToolCallDisplayModel } from "../../../../shared/tool-call-display.js";
import type {
  AgentStreamEvent,
  AgentTimelineItem,
  ToolCallTimelineItem,
} from "../../agent-sdk-types.js";
import { mapOpencodeToolCall } from "./tool-call-mapper.js";

/**
 * OpenCode native-subagent (the `task` tool) parity with Claude.
 *
 * OpenCode runs a `task` subagent inside a CHILD session (`Session.parentID ===`
 * the main session). The subagent's own tool calls (read/grep/bash/…) are emitted
 * as `message.part.updated` events on that child session id — events the main
 * translator otherwise drops. This tracker folds those child tool parts into a
 * live `sub_agent` action log attributed to the parent `task` tool call, mirroring
 * `ClaudeSidechainTracker`.
 *
 * Correlation child-session → parent task callId is learned from the task tool
 * part itself: `state.metadata.sessionId` carries the child id while the task is
 * still running (live), and the completed `state.output` repeats it as
 * `task_id: ses_<child>` as a fallback. Because a child tool part can arrive
 * before the task part that names it, child actions are buffered per child session
 * and flushed once the callId mapping resolves.
 */

type SubAgentActionEntry = {
  index: number;
  toolName: string;
  summary?: string;
};

type SubAgentActivityState = {
  subAgentType?: string;
  description?: string;
  actions: SubAgentActionEntry[];
  actionKeys: string[];
  nextActionIndex: number;
  actionIndexByKey: Map<string, number>;
};

type SubAgentActionCandidate = {
  key: string;
  toolName: string;
  input: unknown;
};

type SubAgentEmitStatus = "running" | "completed" | "failed" | "canceled";

const MAX_SUB_AGENT_LOG_ENTRIES = 200;
const MAX_SUB_AGENT_SUMMARY_CHARS = 160;
const TASK_ID_PATTERN = /task_id:\s*(ses_[A-Za-z0-9]+)/;

function readTrimmedString(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export class OpencodeSidechainTracker {
  /** Child session ids known to belong to a `task` subagent of this agent. */
  private readonly childSessions = new Set<string>();
  /** Child session id → parent `task` tool callId. */
  private readonly taskCallIdByChildSession = new Map<string, string>();
  /** Parent task callId → accumulated activity. */
  private readonly activeByCallId = new Map<string, SubAgentActivityState>();
  /** Child session id → actions seen before the callId mapping resolved. */
  private readonly pendingByChildSession = new Map<string, SubAgentActionCandidate[]>();

  /**
   * Register a discovered child session (from `session.created`/`session.updated`
   * where `info.parentID === mainSessionId`). Lets us recognise its tool parts as
   * subagent activity even before the parent task part names it.
   */
  registerChildSession(sessionId: string): void {
    const normalized = readTrimmedString(sessionId);
    if (normalized) {
      this.childSessions.add(normalized);
    }
  }

  isChildSession(sessionId: string): boolean {
    const normalized = readTrimmedString(sessionId);
    return normalized ? this.childSessions.has(normalized) : false;
  }

  /**
   * Process a `task` tool part observed on the MAIN session. Learns the
   * subAgentType/description and the child-session correlation, flushes any
   * buffered child actions, and returns an enriched `sub_agent` timeline update
   * (sharing the task callId) when something changed.
   */
  handleTaskPart(params: {
    callId: string;
    input: unknown;
    output: unknown;
    metadata: unknown;
    status: SubAgentEmitStatus;
    mcpServerIds: readonly string[];
  }): AgentStreamEvent[] {
    const callId = readTrimmedString(params.callId);
    if (!callId) {
      return [];
    }

    const state = this.getOrCreateState(callId);
    let changed = this.updateContextFromTaskInput(state, params.input);

    const childSessionId = this.resolveChildSessionId(params.metadata, params.output);
    if (childSessionId) {
      this.childSessions.add(childSessionId);
      if (this.taskCallIdByChildSession.get(childSessionId) !== callId) {
        this.taskCallIdByChildSession.set(childSessionId, callId);
      }
      if (this.flushPending(state, childSessionId, params.mcpServerIds)) {
        changed = true;
      }
    }

    if (!changed) {
      return [];
    }
    return [this.buildEvent(callId, params.status, state)];
  }

  /**
   * Process a tool part observed on a CHILD session. Folds it into the parent
   * task's action log when the correlation is known, or buffers it until the
   * task part resolves the mapping. Returns an enriched `sub_agent` update for
   * the parent task callId (status "running" — the subagent is still active).
   */
  handleChildToolPart(params: {
    childSessionId: string;
    toolName: string;
    callId: string | undefined;
    input: unknown;
    mcpServerIds: readonly string[];
  }): AgentStreamEvent[] {
    const childSessionId = readTrimmedString(params.childSessionId);
    const toolName = readTrimmedString(params.toolName);
    if (!childSessionId || !toolName) {
      return [];
    }
    this.childSessions.add(childSessionId);

    const candidate: SubAgentActionCandidate = {
      key: readTrimmedString(params.callId) ?? `${childSessionId}:${toolName}`,
      toolName,
      input: params.input ?? null,
    };

    const taskCallId = this.taskCallIdByChildSession.get(childSessionId);
    if (!taskCallId) {
      this.bufferPending(childSessionId, candidate);
      return [];
    }

    const state = this.getOrCreateState(taskCallId);
    if (!this.appendAction(state, candidate, params.mcpServerIds)) {
      return [];
    }
    return [this.buildEvent(taskCallId, "running", state)];
  }

  clear(): void {
    this.childSessions.clear();
    this.taskCallIdByChildSession.clear();
    this.activeByCallId.clear();
    this.pendingByChildSession.clear();
  }

  private getOrCreateState(callId: string): SubAgentActivityState {
    const existing = this.activeByCallId.get(callId);
    if (existing) {
      return existing;
    }
    const created: SubAgentActivityState = {
      actions: [],
      actionKeys: [],
      nextActionIndex: 1,
      actionIndexByKey: new Map<string, number>(),
    };
    this.activeByCallId.set(callId, created);
    return created;
  }

  private resolveChildSessionId(metadata: unknown, output: unknown): string | undefined {
    const metadataSessionId = readTrimmedString(readRecord(metadata)?.sessionId);
    if (metadataSessionId) {
      return metadataSessionId;
    }
    const outputText = typeof output === "string" ? output : undefined;
    if (!outputText) {
      return undefined;
    }
    const match = TASK_ID_PATTERN.exec(outputText);
    return match?.[1];
  }

  private updateContextFromTaskInput(state: SubAgentActivityState, input: unknown): boolean {
    const record = readRecord(input);
    if (!record) {
      return false;
    }
    const nextSubAgentType = this.normalizeText(record.subagent_type ?? record.subAgentType);
    const nextDescription = this.normalizeText(record.description);

    let changed = false;
    if (nextSubAgentType && nextSubAgentType !== state.subAgentType) {
      state.subAgentType = nextSubAgentType;
      changed = true;
    }
    if (nextDescription && nextDescription !== state.description) {
      state.description = nextDescription;
      changed = true;
    }
    return changed;
  }

  private bufferPending(childSessionId: string, candidate: SubAgentActionCandidate): void {
    const buffered = this.pendingByChildSession.get(childSessionId) ?? [];
    const existing = buffered.findIndex((entry) => entry.key === candidate.key);
    if (existing >= 0) {
      buffered[existing] = candidate;
    } else {
      buffered.push(candidate);
    }
    this.pendingByChildSession.set(childSessionId, buffered);
  }

  private flushPending(
    state: SubAgentActivityState,
    childSessionId: string,
    mcpServerIds: readonly string[],
  ): boolean {
    const buffered = this.pendingByChildSession.get(childSessionId);
    if (!buffered || buffered.length === 0) {
      return false;
    }
    let changed = false;
    for (const candidate of buffered) {
      if (this.appendAction(state, candidate, mcpServerIds)) {
        changed = true;
      }
    }
    this.pendingByChildSession.delete(childSessionId);
    return changed;
  }

  private appendAction(
    state: SubAgentActivityState,
    candidate: SubAgentActionCandidate,
    mcpServerIds: readonly string[],
  ): boolean {
    const display = this.deriveAction(candidate.toolName, candidate.input, mcpServerIds);
    const normalizedToolName = display.toolName;
    const summary = display.summary;
    const existingIndex = state.actionIndexByKey.get(candidate.key);

    if (existingIndex !== undefined) {
      const existing = state.actions[existingIndex];
      if (!existing) {
        return false;
      }
      const nextSummary = existing.summary ?? summary;
      if (existing.toolName === normalizedToolName && existing.summary === nextSummary) {
        return false;
      }
      state.actions[existingIndex] = {
        ...existing,
        toolName: normalizedToolName,
        ...(nextSummary ? { summary: nextSummary } : {}),
      };
      return true;
    }

    state.actions.push({
      index: state.nextActionIndex,
      toolName: normalizedToolName,
      ...(summary ? { summary } : {}),
    });
    state.nextActionIndex += 1;
    state.actionKeys.push(candidate.key);
    this.trimTail(state);
    this.rebuildActionIndex(state);
    return true;
  }

  private trimTail(state: SubAgentActivityState): void {
    while (state.actions.length > MAX_SUB_AGENT_LOG_ENTRIES) {
      state.actions.shift();
      state.actionKeys.shift();
    }
  }

  private rebuildActionIndex(state: SubAgentActivityState): void {
    state.actionIndexByKey.clear();
    for (let index = 0; index < state.actionKeys.length; index += 1) {
      const key = state.actionKeys[index];
      if (key) {
        state.actionIndexByKey.set(key, index);
      }
    }
  }

  private deriveAction(
    toolName: string,
    input: unknown,
    mcpServerIds: readonly string[],
  ): { toolName: string; summary?: string } {
    const item = mapOpencodeToolCall({
      toolName,
      callId: `sub-agent-summary-${toolName}`,
      status: "running",
      input,
      mcpServerIds,
    });
    if (!item) {
      return { toolName };
    }
    const display = buildToolCallDisplayModel({
      name: item.name,
      status: item.status,
      error: item.error,
      detail: item.detail,
      ...(item.metadata ? { metadata: item.metadata } : {}),
    });
    return {
      toolName: item.name,
      ...(this.normalizeText(display.summary)
        ? { summary: this.normalizeText(display.summary) }
        : {}),
    };
  }

  private normalizeText(value: unknown): string | undefined {
    const normalized = readTrimmedString(value)?.replace(/\s+/g, " ");
    if (!normalized) {
      return undefined;
    }
    if (normalized.length <= MAX_SUB_AGENT_SUMMARY_CHARS) {
      return normalized;
    }
    return `${normalized.slice(0, MAX_SUB_AGENT_SUMMARY_CHARS)}...`;
  }

  private buildEvent(
    callId: string,
    status: SubAgentEmitStatus,
    state: SubAgentActivityState,
  ): AgentStreamEvent {
    const detail: Extract<AgentTimelineItem, { type: "tool_call" }>["detail"] = {
      type: "sub_agent",
      ...(state.subAgentType ? { subAgentType: state.subAgentType } : {}),
      ...(state.description ? { description: state.description } : {}),
      log: state.actions
        .map((action) =>
          action.summary ? `[${action.toolName}] ${action.summary}` : `[${action.toolName}]`,
        )
        .join("\n"),
      actions: state.actions.map((action) => ({
        index: action.index,
        toolName: action.toolName,
        ...(action.summary ? { summary: action.summary } : {}),
      })),
    };

    const item: ToolCallTimelineItem = {
      type: "tool_call",
      callId,
      name: "task",
      status,
      detail,
      error: null,
    };

    return {
      type: "timeline",
      item,
      provider: "opencode",
    };
  }
}
